<script>
import { mapGetters } from 'vuex';
import { useSectorlessAccount } from 'dashboard/composables/useSectorlessAccount';
import V4Button from 'dashboard/components-next/button/Button.vue';
import { useAlert, useTrack } from 'dashboard/composables';
import ReportFilterSelector from './components/FilterSelector.vue';
import { GROUP_BY_FILTER } from './constants';
import { REPORTS_EVENTS } from '../../../../helper/AnalyticsHelper/events';
import { generateFileName } from 'dashboard/helper/downloadHelper';
import ReportContainer from './ReportContainer.vue';
import ReportHeader from './components/ReportHeader.vue';
import ToggleSwitch from 'dashboard/components-next/switch/Switch.vue';

const REPORTS_KEYS = {
  CONVERSATIONS: 'conversations_count',
  INCOMING_MESSAGES: 'incoming_messages_count',
  OUTGOING_MESSAGES: 'outgoing_messages_count',
  FIRST_RESPONSE_TIME: 'avg_first_response_time',
  RESOLUTION_TIME: 'avg_resolution_time',
  RESOLUTION_COUNT: 'resolutions_count',
  REPLY_TIME: 'reply_time',
};

export default {
  name: 'ConversationReports',
  components: {
    ReportHeader,
    ReportFilterSelector,
    ReportContainer,
    V4Button,
    ToggleSwitch,
  },
  setup() {
    return useSectorlessAccount();
  },
  data() {
    return {
      from: 0,
      to: 0,
      groupBy: GROUP_BY_FILTER[1],
      businessHours: false,
      selectedSector: null,
    };
  },
  computed: {
    ...mapGetters([
      'getCurrentAccount',
      'getCurrentSectorRole',
      'getCurrentRole',
      'sectors/getSectors',
    ]),
    isAccountAdmin() {
      return ['administrator', 'super_admin'].includes(this.getCurrentRole);
    },
    userSectorIds() {
      const memberships = this.getCurrentAccount?.sector_memberships || [];
      return memberships
        .filter(membership => membership.role === 'sector_admin')
        .map(membership => membership.sector_id);
    },
    sectorOptions() {
      if (this.isAccountAdmin) {
        return [
          { id: null, name: 'Todos os setores' },
          ...this['sectors/getSectors'],
        ];
      }

      return this['sectors/getSectors'].filter(sector =>
        this.userSectorIds.includes(sector.id)
      );
    },
    reportSectorId() {
      if (this.isAccountAdmin) {
        return this.selectedSector?.id ?? null;
      }

      if (this.getCurrentSectorRole === 'sector_admin') {
        return (
          this.selectedSector?.id ||
          this.getCurrentAccount.sector_memberships?.[0]?.sector_id ||
          null
        );
      }

      return null;
    },
  },
  watch: {
    sectorOptions: {
      immediate: true,
      handler(options) {
        if (!options.length) {
          return;
        }

        if (!this.selectedSector) {
          this.selectedSector = options[0];
        }
      },
    },
  },
  mounted() {
    this.$store.dispatch('sectors/get');
  },
  methods: {
    fetchAllData() {
      this.fetchAccountSummary();
      this.fetchChartData();
    },
    fetchAccountSummary() {
      try {
        this.$store.dispatch('fetchAccountSummary', this.getRequestPayload());
      } catch {
        useAlert(this.$t('REPORT.SUMMARY_FETCHING_FAILED'));
      }
    },
    fetchChartData() {
      [
        'CONVERSATIONS',
        'INCOMING_MESSAGES',
        'OUTGOING_MESSAGES',
        'FIRST_RESPONSE_TIME',
        'RESOLUTION_TIME',
        'RESOLUTION_COUNT',
        'REPLY_TIME',
      ].forEach(async key => {
        try {
          await this.$store.dispatch('fetchAccountReport', {
            metric: REPORTS_KEYS[key],
            ...this.getRequestPayload(),
          });
        } catch {
          useAlert(this.$t('REPORT.DATA_FETCHING_FAILED'));
        }
      });
    },
    getRequestPayload() {
      const { from, to, groupBy, businessHours, reportSectorId } = this;

      return {
        from,
        to,
        groupBy: groupBy?.period,
        businessHours,
        sectorId: reportSectorId,
      };
    },
    downloadAgentReports() {
      const { from, to } = this;
      const fileName = generateFileName({
        type: 'agent',
        to,
        businessHours: this.businessHours,
      });
      this.$store.dispatch('downloadAgentReports', {
        from,
        to,
        fileName,
        businessHours: this.businessHours,
        sectorId: this.reportSectorId,
      });
    },
    onSectorChange() {
      if (!this.from || !this.to) {
        return;
      }

      this.fetchAllData();

      useTrack(REPORTS_EVENTS.FILTER_REPORT, {
        filterValue: {
          from: this.from,
          to: this.to,
          groupBy: this.groupBy,
          businessHours: this.businessHours,
          sectorId: this.reportSectorId,
        },
        reportType: 'conversations',
      });
    },
    onBusinessHoursChange() {
      if (!this.from || !this.to) {
        return;
      }

      this.fetchAllData();

      useTrack(REPORTS_EVENTS.FILTER_REPORT, {
        filterValue: {
          from: this.from,
          to: this.to,
          groupBy: this.groupBy,
          businessHours: this.businessHours,
          sectorId: this.reportSectorId,
        },
        reportType: 'conversations',
      });
    },
    onFilterChange({ from, to, groupBy }) {
      this.from = from;
      this.to = to;
      this.groupBy = groupBy;
      this.fetchAllData();

      useTrack(REPORTS_EVENTS.FILTER_REPORT, {
        filterValue: {
          from,
          to,
          groupBy,
          businessHours: this.businessHours,
          sectorId: this.reportSectorId,
        },
        reportType: 'conversations',
      });
    },
  },
};
</script>

<template>
  <ReportHeader :header-title="$t('REPORT.HEADER')">
    <V4Button
      :label="$t('REPORT.DOWNLOAD_AGENT_REPORTS')"
      icon="i-ph-download-simple"
      size="sm"
      @click="downloadAgentReports"
    />
  </ReportHeader>
  <div class="flex flex-col gap-3">
    <div class="flex flex-wrap gap-2 items-start">
      <ReportFilterSelector
        :show-agents-filter="false"
        :show-business-hours-switch="false"
        show-group-by-filter
        @filter-change="onFilterChange"
      />
      <div
        v-if="!isSectorlessAccount && sectorOptions.length"
        class="multiselect-wrap--small"
      >
        <multiselect
          v-model="selectedSector"
          :placeholder="$t('WHATSAPP_PRICING_REPORTS.FILTER.SECTOR')"
          label="name"
          track-by="id"
          :options="sectorOptions"
          :show-labels="false"
          :allow-empty="false"
          :disabled="!isAccountAdmin"
          @update:model-value="onSectorChange"
        />
      </div>
      <div class="ml-auto flex items-center pt-2">
        <span class="mx-2 text-sm whitespace-nowrap">
          {{ $t('REPORT.BUSINESS_HOURS') }}
        </span>
        <ToggleSwitch v-model="businessHours" @change="onBusinessHoursChange" />
      </div>
    </div>
    <ReportContainer :group-by="groupBy" />
  </div>
</template>
